import React, { useEffect, useRef, useState } from 'react';
import {
  Image,
  ImageBackground,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Platform as ReCaptchaPlatform } from 'apollo/queries/autogenerate/schemas-public';
import Icon from 'components/Icon';
import typography from 'constants/typography';
import IMAGES from 'shared/constants/images';
import EditUserPhoneUnauthenticated from 'shared/modules/account/components/organisms/EditUserPhoneUnauthenticated';
import PinCode from 'shared/modules/auth/components/organisms/PinCode';
import PinCodeLockOut from 'shared/modules/auth/components/organisms/PinCodeLockOut';
import ReSignIn from 'shared/modules/auth/components/organisms/ReSignIn';
import SignIn, {
  CustomErrorCode,
} from 'shared/modules/auth/components/organisms/SignIn';
import {
  ACTION,
  EVENT_TYPE,
  FAIL,
  ITEM,
  SCREEN,
  SUCCESS,
} from 'shared/modules/auth/constants/mixpanel';
import Button from 'shared/modules/common/components/atoms/Button';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import useSendAppData from 'shared/modules/common/hooks/use-send-app-data';
import eventBus from 'shared/utils/event-bus';
import Mixpanel from 'shared/vendor/Mixpanel';
import AppMockups from 'web/assets/images/app-mockups.png';
import BackgroundDesktop from 'web/assets/images/background-desktop.jpg';
import BackgroundMobile from 'web/assets/images/background-mobile.jpg';
import Beginner from 'web/assets/images/beginner.png';
import LinkExternal from 'web/assets/images/link-external.png';
import Logo from 'web/assets/images/logo-white.png';
import Question from 'web/assets/images/question.png';
import useReCaptcha from 'web/modules/auth/hooks/use-recaptcha';
import EmailReminderUnauthenticatedDialog from 'web/modules/auth/organisms/EmailReminderUnauthenticatedDialog';
import AppStoreBadgeLinks from 'web/modules/common/components/molecules/AppStoreBadgeLinks';

const EVENT_ERROR = `${SCREEN.LOGIN} - ${ACTION.ERROR}`;
const EVENT_CLICK = `${SCREEN.LOGIN} - ${ACTION.CLICK}`;
const EVENT_EDIT = `${SCREEN.LOGIN} - ${ACTION.FIELD_EDIT}`;
const EVENT_VIEW = `${SCREEN.LOGIN} - ${ACTION.PAGE_VIEW}`;

const FORGOT_CLICK_EVENT = `${SCREEN.FORGOT_LOGIN_DETAILS} - ${ACTION.CLICK}`;

const windowHeight = document.documentElement.clientHeight;

const SignInScreen = () => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const isHugeScreen = useMediaQuery({ minWidth: 1024 });

  // Send application data on successful signin
  const sendAppData = useSendAppData();

  const sessionCredentials = useRef({
    email: '',
    phone: '',
    sessionToken: '',
  });

  const location = useLocation();
  const navigate = useNavigate();

  const { getToken, error: reCaptchaError } = useReCaptcha(
    // TODO: We need to move these keys somewhere
    process.env.WEB_ENV === 'production'
      ? '6LeCmD8eAAAAAIfvACm4Mai8uXeutmD840ae6hCH'
      : '6Lf89K4eAAAAAEb2G0lSJjzZCegPbdhn4UIzH0kT'
  );

  // If ?reset=1 is present in the query params, show the Reset Phone dialog
  const [showEmailReminderDialog, setShowEmailReminderDialog] = useState(false);
  const [isOTP, setIsOTP] = useState(false);
  const [showPinCodeLockOut, setShowPinCodeLockOut] = useState(false);
  const [showReSignIn, setShowReSignIn] = useState(false);
  const [
    showForgotEmailOrResetPhoneModal,
    setShowForgotEmailOrResetPhoneModal,
  ] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showResetPhoneDialog, setShowResetPhoneDialog] = useState(
    searchParams.get('reset') === '1'
  );
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  useEffect(() => {
    Mixpanel.timeEvent(EVENT_VIEW);

    return () => Mixpanel.trackEvent(EVENT_VIEW);
  }, []);

  useEffect(() => {
    if (reCaptchaError) {
      Mixpanel.trackEvent(EVENT_ERROR, {
        Error: FAIL.RECAPTCHA,
      });
    }
  }, [reCaptchaError]);

  // If user has been logged out because of expired tokens, the ReSignIn message should show.
  useEffect(() => {
    if (location.state?.reSignIn) {
      setShowReSignIn(true);
    }
  }, [location]);

  const [customErrorCode, setCustomErrorCode] = useState<CustomErrorCode>();

  const onResetPhoneOrForgotEmailPress = () => {
    Mixpanel.trackEvent(FORGOT_CLICK_EVENT, {
      Item: ITEM.CHANGE_PHONE_OR_REMIND_EMAIL,
    });

    setShowForgotEmailOrResetPhoneModal(true);
  };

  const onResetPhonePress = () => {
    Mixpanel.trackEvent(EVENT_CLICK, {
      Item: ITEM.CHANGE_PHONE,
    });

    setShowResetPhoneDialog(true);
  };

  const onForgotEmailPress = () => {
    Mixpanel.trackEvent(FORGOT_CLICK_EVENT, {
      Item: ITEM.FORGOT_EMAIL_ADDRESS,
    });

    setShowEmailReminderDialog(true);
  };

  const onClose = () => {
    setShowErrorDialog(false);
  };

  return (
    <ImageBackground
      source={{ uri: isBigScreen ? BackgroundDesktop : BackgroundMobile }}
      style={style.root}
      imageStyle={!isBigScreen && style.rootImage}
    >
      <View accessibilityRole="navigation" style={style.header}>
        <TouchableOpacity
          accessibilityRole="link"
          href="https://paidy.com"
          hrefAttrs={{ target: '_blank' }}
        >
          <Image source={{ uri: Logo }} style={style.headerImage} />
        </TouchableOpacity>
      </View>
      <View style={style.mainWrapper}>
        <View
          accessibilityRole="main"
          style={[style.main, isHugeScreen && style.mainHugeScreen]}
        >
          <View style={[style.card, style.marginBottom]}>
            {!isOTP && !showPinCodeLockOut && !showReSignIn && (
              <>
                <SignIn
                  onFocus={(fieldName) => {
                    Mixpanel.trackEvent(EVENT_EDIT, {
                      Item: fieldName === 'email' ? ITEM.EMAIL : ITEM.PHONE,
                    });
                  }}
                  onSubmit={() => {
                    Mixpanel.trackEvent(EVENT_CLICK, {
                      Item: ITEM.SUBMIT,
                    });
                  }}
                  onSuccess={(props) => {
                    sessionCredentials.current = props;
                    setCustomErrorCode(undefined);
                    setIsOTP(true);
                  }}
                  onError={(error) => {
                    const errorCode: number = (error as any).graphQLErrors[0]
                      ?.extensions.code;

                    if (errorCode) {
                      if (errorCode === 409) {
                        setCustomErrorCode(CustomErrorCode.AUTH_FAIL);
                      } else if (errorCode === 403) {
                        setCustomErrorCode(undefined);
                        setShowPinCodeLockOut(true);
                      } else if (errorCode === 503) {
                        setShowErrorDialog(true);
                      }
                    } else {
                      // Network error, likely a WAF block
                      /*
                       * Note: Since we currently have a CORS issue with the WAF
                       * (the WAF is currently unable to provide the correct
                       * CORS response headers) the browser does not allow us
                       * to see the 406 error from the WAF response. Therefore,
                       * we do not currently have a way to determine between a
                       * network error (no server response) and a WAF block
                       * response (406). For now, we will handle the network
                       * error as a WAF block, as this is likely to be the case.
                       */
                      setCustomErrorCode(CustomErrorCode.WAF_FAIL);
                    }
                  }}
                  customErrorCode={customErrorCode}
                  reCaptchaPlatform={ReCaptchaPlatform.WEB}
                  getRecaptchaTokenFunction={() => getToken('WEB/AUTHENTICATE')}
                />
                {!isOTP && !showPinCodeLockOut && !showReSignIn && (
                  <TouchableOpacity
                    onPress={onResetPhoneOrForgotEmailPress}
                    accessibilityRole="link"
                  >
                    <Text style={style.link}>
                      メールアドレスを忘れた場合・携帯電話番号の変更
                    </Text>
                  </TouchableOpacity>
                )}
                <View style={style.description}>
                  <View style={style.titleWrapper}>
                    <Image source={{ uri: Beginner }} style={style.icon} />
                    <Text style={style.title}>初めてご利用の方へ</Text>
                  </View>
                  <Text style={typography.caption02}>
                    今回登録するメールアドレスと携帯電話番号は、今後お支払いやログインなどで必要となります。お間違いのないようご注意ください。
                  </Text>

                  <View style={style.titleWrapper}>
                    <Image source={{ uri: Question }} style={style.icon} />
                    <Text style={style.title}>
                      メールアドレスが利用できなくなった場合
                    </Text>
                  </View>
                  <Text style={typography.caption02}>
                    登録したメールアドレスが利用できなくなった場合も、ログイン情報として入力することでログイン可能です。
                  </Text>
                  <ReCaptchaBranding />
                </View>
              </>
            )}
            {isOTP && !showPinCodeLockOut && !showReSignIn && (
              <PinCode
                sessionCredentials={sessionCredentials.current}
                onFocus={() =>
                  Mixpanel.trackEvent(EVENT_EDIT, {
                    Item: ITEM.PIN,
                  })
                }
                onSuccess={async () => {
                  Mixpanel.trackEvent(SUCCESS.AUTHENTICATION, {
                    'Event Type': EVENT_TYPE.SMS,
                  });

                  await sendAppData({
                    deviceType: 'WEB',
                    appVersion: process.env.APP_VERSION,
                  });

                  setCustomErrorCode(undefined);
                  eventBus.emit('auth:set', true);

                  // Check the existence of location.state.next
                  // Redirect the user to that page on sign in
                  if (location.state?.next) {
                    // Clear the History state to avoid issues such as the SignIn screen accidentally picking
                    // up the wrong path to redirect
                    // TIP: We always clear the History state when we are done with it.
                    navigate(location.state.next, { state: null });
                  }
                }}
                onError={(error) => {
                  Mixpanel.trackEvent(FAIL.AUTHENTICATION, {
                    'Event Type': EVENT_TYPE.SMS,
                  });

                  setCustomErrorCode(CustomErrorCode.OTP_FAIL);
                  setIsOTP(false);
                  const errorCode: number = (error as any).graphQLErrors[0]
                    ?.extensions.code;

                  if (errorCode === 503) {
                    setShowErrorDialog(true);
                  }
                }}
                reCaptchaPlatform={ReCaptchaPlatform.WEB}
                getRecaptchaTokenFunction={() => getToken('WEB/AUTHENTICATE')}
              />
            )}
            {showPinCodeLockOut && (
              <PinCodeLockOut onPress={() => setShowPinCodeLockOut(false)} />
            )}
            {showReSignIn && (
              <ReSignIn onPress={() => setShowReSignIn(false)} />
            )}
            {isOTP && (
              <View style={style.cardLink}>
                <TouchableOpacity
                  accessibilityRole="link"
                  href="https://terms.paidy.com"
                  hrefAttrs={{ target: '_blank' }}
                >
                  <Text style={style.cardLinkText}>
                    個人情報取扱に関する同意条項に同意して
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
          <View
            style={[
              style.promoTextWrapper,
              isHugeScreen && style.promoTextWrapperHugeScreen,
            ]}
          >
            <Text style={style.promoTextHeader}>ペイディアプリで、</Text>
            <Text style={[style.promoTextHeader, style.marginBottom]}>
              もっと便利に。
            </Text>
            <Text
              style={[
                style.promoText,
                isHugeScreen && style.promoTextHugeScreen,
              ]}
            >
              アプリから本人確認すると、{'\n'}3・6・12回あと払いや{'\n'}
              ペイディカードが利用可能に。
            </Text>
            <Text
              accessibilityRole="link"
              href="https://paidy.com/npay"
              hrefAttrs={{ target: '_blank' }}
              style={style.marginBottom}
            >
              <Text style={style.textHighlight}>もっと詳しく</Text>
              <Image source={{ uri: LinkExternal }} style={style.linkIcon} />
            </Text>
            {isHugeScreen && <AppStoreBadgeLinks />}
          </View>
          <Image
            source={{ uri: AppMockups }}
            style={[style.appMockup, style.marginBottom]}
          />
          {!isHugeScreen && <AppStoreBadgeLinks />}
        </View>
      </View>
      <View style={style.footer}>
        <Text style={style.footerText}>© Paidy Inc.</Text>
      </View>
      <Dialog
        title=""
        open={showForgotEmailOrResetPhoneModal}
        style={!isBigScreen ? style.dialogSmallScreen : style.dialogBigScreen}
        onCloseButtonPress={() => {
          setShowForgotEmailOrResetPhoneModal(false);
        }}
        backdropStyle={!isBigScreen && style.dialogBackdropSmallScreen}
      >
        <View>
          <TouchableOpacity
            style={[style.selectDialogOption, style.selectDialogOptionFirst]}
            onPress={onForgotEmailPress}
          >
            <Text style={style.selectDialogOptionLabel}>
              メールアドレスを忘れた場合
            </Text>
            <Icon
              name="back-arrow"
              tintColor={'#8a8a8a'}
              size="24"
              style={style.selectDialogArrow}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={style.selectDialogOption}
            onPress={onResetPhonePress}
          >
            <Text style={style.selectDialogOptionLabel}>
              携帯電話番号の変更
            </Text>
            <Icon
              name="back-arrow"
              tintColor={'#8a8a8a'}
              size="24"
              style={style.selectDialogArrow}
            />
          </TouchableOpacity>
        </View>
      </Dialog>
      <EditUserPhoneUnauthenticated
        isVisible={showResetPhoneDialog}
        onNavigateBack={() => {
          setSearchParams({}); // Remove the ?reset=1 query param when dialog closes
          setShowResetPhoneDialog(false);
        }}
        onClose={() => {
          setShowForgotEmailOrResetPhoneModal(false);
          setShowResetPhoneDialog(false);
        }}
        onSuccess={() => setShowResetPhoneDialog(false)}
        reCaptchaPlatform={ReCaptchaPlatform.WEB}
        getRecaptchaTokenFunction={() => getToken('WEB/RESET_PHONE')}
      />
      <EmailReminderUnauthenticatedDialog
        isVisible={showEmailReminderDialog}
        onNavigateBack={() => {
          setShowEmailReminderDialog(false);
        }}
        onClose={() => {
          setShowForgotEmailOrResetPhoneModal(false);
          setShowEmailReminderDialog(false);
        }}
        reCaptchaPlatform={ReCaptchaPlatform.WEB}
        getRecaptchaTokenFunction={() => getToken('WEB/REMIND_EMAIL')}
      />
      <Dialog
        title=""
        open={showErrorDialog}
        style={style.dialog}
        onCloseButtonPress={onClose}
      >
        <View style={style.container}>
          <View style={style.topSectionCenter}>
            <Image source={IMAGES.exclaimTriangle} style={style.errorImage} />

            <Text style={style.errorTitle}>エラーが発生しました</Text>

            <Text style={style.body}>
              しばらく時間をおいてから{`\n`}もう一度お試しください。
            </Text>
          </View>
          <View style={style.buttonContainer}>
            <Button
              title={'OK'}
              onPress={() => {
                setShowErrorDialog(false);
              }}
              style={style.button}
              id="server-error-dialog"
            />
          </View>
        </View>
      </Dialog>
    </ImageBackground>
  );
};

// GOOGLE requires us to show this in exchange to hiding the badge
const ReCaptchaBranding = () => (
  <View style={style.reCaptchaBranding}>
    <Text style={style.reCaptchaBrandingText}>
      このサイトはreCAPTCHAによって保護されており、Googleの
      <Text
        style={[style.reCaptchaBrandingText, style.reCaptchaBrandingLinkText]}
        accessibilityRole="link"
        href="https://policies.google.com/privacy?hl=ja"
        hrefAttrs={{
          target: '_blank',
        }}
      >
        プライバシー ポリシー
      </Text>
      および
      <Text
        style={[style.reCaptchaBrandingText, style.reCaptchaBrandingLinkText]}
        accessibilityRole="link"
        href="https://policies.google.com/terms?hl=ja"
        hrefAttrs={{
          target: '_blank',
        }}
      >
        利用規約
      </Text>
      に準拠しています。
    </Text>
  </View>
);

const style = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
  },
  rootImage: {
    // To simulate background-position: top, we need to "stretch" the image but keep the aspect ratio
    resizeMode: 'stretch',
    aspectRatio: 0.22,
  },
  header: {
    padding: 24,
    alignItems: 'center',
  },
  headerImage: {
    width: 104,
    height: 40,
    resizeMode: 'cover',
  },
  mainWrapper: {
    alignItems: 'center',
  },
  main: {
    alignItems: 'center',
    width: '100%',
    maxWidth: 1200,
  },
  mainHugeScreen: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-around',
  },
  promoTextWrapper: {
    alignItems: 'center',
  },
  promoTextWrapperHugeScreen: {
    alignItems: 'flex-start',
  },
  promoTextHeader: {
    backgroundColor: '#1C1C1C',
    color: 'white',
    fontSize: 28,
    fontWeight: 'bold',
    paddingHorizontal: 16,
    paddingVertical: 2,
    marginBottom: 4,
    borderRadius: 8,
  },
  promoText: {
    color: '#1C1C1C',
    marginBottom: 24,
    fontSize: 20,
    textAlign: 'center',
  },
  promoTextHugeScreen: {
    textAlign: 'auto',
  },
  linkIcon: {
    width: 16,
    height: 16,
    resizeMode: 'contain',
    marginLeft: 4,
  },
  appMockup: {
    width: 344,
    height: 344,
    resizeMode: 'contain',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    minHeight: 600,
    overflow: 'hidden',
    maxWidth: 424,
    width: '100%',
    justifyContent: 'space-between',
  },
  cardLink: {
    backgroundColor: '#F0F0F0',
    margin: -24,
    marginTop: 24,
    padding: 8,
  },
  cardLinkText: {
    textAlign: 'center',
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  footer: {
    padding: 24,
    alignItems: 'center',
  },
  footerText: {
    color: 'white',
    fontSize: 12,
  },
  textHighlight: {
    color: '#E52581',
    textDecorationLine: 'underline',
  },
  marginBottom: {
    marginBottom: 24,
  },
  reCaptchaBranding: {
    marginTop: 24,
  },
  reCaptchaBrandingText: {
    fontSize: 12,
    color: '#a1aab0',
  },
  reCaptchaBrandingLinkText: {
    textDecorationLine: 'underline',
  },
  dialogBigScreen: {
    maxWidth: 424,
    minHeight: 232,
    height: 232,
  },
  dialogSmallScreen: {
    height: 232,
    margin: 'auto',
    maxWidth: 424,
    borderRadius: 12,
  },
  description: {
    margin: -24,
    paddingHorizontal: 24,
    paddingBottom: 24,
    backgroundColor: '#f6f8fa',
  },
  link: {
    fontSize: 12,
    textAlign: 'center',
    color: '#E52581',
    textDecorationLine: 'underline',
    marginBottom: 40,
  },
  title: {
    color: '#4c525b',
    fontSize: 14,
    fontWeight: '600',
  },
  icon: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  titleWrapper: {
    marginTop: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectDialogOption: {
    width: '100%',
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: '#f0f0f0',
    borderRadius: 12,
  },
  selectDialogOptionFirst: {
    marginBottom: 24,
  },
  selectDialogArrow: {
    transform: [
      {
        rotate: '180deg',
      },
    ],
  },
  selectDialogOptionLabel: {
    fontSize: 14,
    lineHeight: 22,
    color: '#1c1c1c',
    fontWeight: '300',
  },
  container: {
    flex: 1,
  },
  topSectionCenter: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#2a2e32',
    marginBottom: 24,
  },
  body: {
    fontWeight: '300',
    color: '#2a2e32',
  },
  button: {
    alignSelf: 'center',
    marginVertical: 8,
  },
  buttonContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  errorImage: {
    width: 184,
    height: 184,
    alignSelf: 'center',
  },
  dialogBackdropSmallScreen: {
    height: windowHeight,
    paddingHorizontal: 20,
  },
  dialog: {
    maxHeight: 624,
  },
});

export default SignInScreen;
